<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    header="Edit Order"
    :style="{ width: '28rem' }"
  >
    <!-- Context card -->
    <div
      class="border-navy-100 bg-navy-50 mb-6 flex items-center gap-2.5 rounded-[10px] border px-3.5 py-2.5"
    >
      <div
        class="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-md bg-amber-50"
      >
        <i class="pi pi-file text-sm text-amber-600" />
      </div>
      <div class="min-w-0">
        <p class="text-navy-800 truncate text-[13.5px] font-semibold">
          {{ order.displayHeadline }}
        </p>
        <p v-if="contextSubline" class="text-navy-400 truncate text-xs">
          {{ contextSubline }}
        </p>
      </div>
    </div>

    <form id="edit-order-form" @submit.prevent="save">
      <!-- Order Details -->
      <div class="mb-7">
        <div
          class="text-navy-400 mb-3.5 flex items-center gap-2 text-[11px] font-bold tracking-wider uppercase"
        >
          Order Details
          <span class="bg-navy-100 h-px flex-1" />
        </div>

        <div class="mb-4">
          <label
            for="description"
            class="text-navy-700 mb-1.5 block text-[13px] font-semibold"
          >
            Description
          </label>
          <textarea
            id="description"
            v-model="form.description"
            rows="3"
            placeholder="Enter a description for this order"
            :disabled="isImported"
            :class="[
              'border-navy-200 text-navy-800 placeholder-navy-300 block w-full resize-y rounded-[10px] border-[1.5px] px-3.5 py-2.5 text-sm transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10 focus:outline-none',
              isImported
                ? 'cursor-not-allowed bg-neutral-50 opacity-60'
                : 'hover:border-navy-300',
            ]"
          />
          <p
            v-if="isImported"
            class="text-navy-400 mt-1.5 flex items-center gap-1 text-xs"
          >
            <i class="pi pi-lock text-[10px]" />
            Managed by {{ agentName }}
          </p>
          <p v-else class="text-navy-400 mt-1.5 text-xs">
            First line becomes the headline. Additional lines appear as the
            subline.
          </p>
        </div>

        <div>
          <label
            for="fileNumber"
            class="text-navy-700 mb-1.5 block text-[13px] font-semibold"
          >
            File Number
          </label>
          <input
            id="fileNumber"
            v-model="form.externalFileNumber"
            type="text"
            placeholder="External file number (optional)"
            :disabled="isImported"
            :class="[
              'border-navy-200 text-navy-800 placeholder-navy-300 block w-full rounded-[10px] border-[1.5px] px-3.5 py-2.5 text-sm transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10 focus:outline-none',
              isImported
                ? 'cursor-not-allowed bg-neutral-50 opacity-60'
                : 'hover:border-navy-300',
            ]"
          />
          <p
            v-if="isImported"
            class="text-navy-400 mt-1.5 flex items-center gap-1 text-xs"
          >
            <i class="pi pi-lock text-[10px]" />
            Managed by {{ agentName }}
          </p>
        </div>
      </div>

      <!-- Assignment -->
      <div class="mb-7">
        <div
          class="text-navy-400 mb-3.5 flex items-center gap-2 text-[11px] font-bold tracking-wider uppercase"
        >
          Assignment
          <span class="bg-navy-100 h-px flex-1" />
        </div>

        <div>
          <label
            for="officerId"
            class="text-navy-700 mb-1.5 block text-[13px] font-semibold"
          >
            Assigned Officer
          </label>
          <div class="relative">
            <span
              class="bg-navy-700 pointer-events-none absolute top-1/2 left-3 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-[9px] font-bold tracking-wide text-white"
            >
              {{ selectedOfficerInitials }}
            </span>
            <select
              id="officerId"
              v-model="form.officerId"
              :disabled="loadingMembers"
              class="border-navy-200 hover:border-navy-300 block w-full cursor-pointer appearance-none rounded-[10px] border-[1.5px] bg-white py-2.5 pr-10 pl-11 text-sm transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10 focus:outline-none disabled:opacity-50"
            >
              <option v-if="loadingMembers" value="" disabled>
                Loading officers...
              </option>
              <option
                v-if="!loadingMembers && officerMembers.length === 0"
                value=""
                disabled
              >
                No officers configured
              </option>
              <option
                v-for="member in officerMembers"
                :key="member.id"
                :value="member.id"
              >
                {{ member.name }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Settings -->
      <div>
        <div
          class="text-navy-400 mb-3.5 flex items-center gap-2 text-[11px] font-bold tracking-wider uppercase"
        >
          Settings
          <span class="bg-navy-100 h-px flex-1" />
        </div>

        <label
          :class="[
            'flex cursor-pointer items-center gap-4 rounded-[10px] border-[1.5px] p-4 transition-all',
            form.verificationEnabled
              ? 'border-green-500 bg-green-50'
              : 'border-navy-100 bg-neutral-50',
          ]"
        >
          <div
            :class="[
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-md border transition-all',
              form.verificationEnabled
                ? 'border-green-500 bg-green-100'
                : 'border-navy-100 bg-white',
            ]"
          >
            <i
              :class="[
                'pi pi-star-fill text-sm transition-colors',
                form.verificationEnabled ? 'text-green-700' : 'text-navy-400',
              ]"
            />
          </div>
          <div class="flex-1">
            <p class="text-navy-800 text-[13.5px] font-semibold">
              ID Verification
            </p>
            <p class="text-navy-400 mt-0.5 text-xs">
              Require participants to verify their identity before closing
            </p>
          </div>
          <div class="relative h-6 w-11 shrink-0">
            <input
              v-model="form.verificationEnabled"
              type="checkbox"
              class="peer sr-only"
            />
            <div
              class="bg-navy-200 h-6 w-11 rounded-full transition-colors peer-checked:bg-green-600"
            />
            <div
              class="absolute top-[2px] left-[2px] h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5"
            />
          </div>
        </label>

        <label
          :class="[
            'mt-3 flex cursor-pointer items-center gap-4 rounded-[10px] border-[1.5px] p-4 transition-all',
            form.documentEmailEnabled
              ? 'border-green-500 bg-green-50'
              : 'border-navy-100 bg-neutral-50',
          ]"
        >
          <div
            :class="[
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-md border transition-all',
              form.documentEmailEnabled
                ? 'border-green-500 bg-green-100'
                : 'border-navy-100 bg-white',
            ]"
          >
            <i
              :class="[
                'pi pi-envelope text-sm transition-colors',
                form.documentEmailEnabled ? 'text-green-700' : 'text-navy-400',
              ]"
            />
          </div>
          <div class="flex-1">
            <p class="text-navy-800 text-[13.5px] font-semibold">
              Document Email
            </p>
            <p class="text-navy-400 mt-0.5 text-xs">
              Email closing documents to participants after signing
            </p>
          </div>
          <div class="relative h-6 w-11 shrink-0">
            <input
              v-model="form.documentEmailEnabled"
              type="checkbox"
              class="peer sr-only"
            />
            <div
              class="bg-navy-200 h-6 w-11 rounded-full transition-colors peer-checked:bg-green-600"
            />
            <div
              class="absolute top-[2px] left-[2px] h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5"
            />
          </div>
        </label>
      </div>
    </form>

    <template #footer>
      <div class="flex justify-end gap-2.5">
        <AppButton type="button" variant="secondary" @click="visible = false">
          Cancel
        </AppButton>
        <AppButton type="submit" form="edit-order-form" :loading="isSaving">
          Save Changes
        </AppButton>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import Drawer from "primevue/drawer"
import { useToast } from "primevue/usetoast"
import { computed, reactive, ref, watch } from "vue"

import type { MemberNode, OrderType } from "../../pages/Order/types"
import { apolloClient } from "../../queries/client"
import COMPANY_MEMBERS from "../../queries/company/members.gql"
import UPDATE_ORDER from "../../queries/order/update.gql"
import AppButton from "../ui/AppButton.vue"

const props = defineProps<{
  order: OrderType
}>()

const visible = defineModel<boolean>("visible", { required: true })

const emit = defineEmits<{
  success: []
}>()

const toast = useToast()
const isSaving = ref(false)
const loadingMembers = ref(false)
const members = ref<MemberNode[]>([])
const officerMembers = computed(() => members.value.filter((m) => m.officer))

const form = reactive({
  description: "",
  externalFileNumber: "",
  officerId: "",
  verificationEnabled: false,
  documentEmailEnabled: false,
})

const isImported = computed(() => !!props.order.importAgentRegistration)
const agentName = computed(
  () => props.order.importAgentRegistration?.name ?? "source system",
)

const contextSubline = computed(() => {
  const parts: string[] = []

  if (props.order.description) {
    const rest = props.order.description.split("\n").slice(1).join(" ").trim()
    if (rest) parts.push(rest)
  }

  if (props.order.displaySubline) parts.push(props.order.displaySubline)
  if (props.order.externalFileNumber) parts.push(props.order.externalFileNumber)

  return parts.join(" · ") || null
})

const selectedOfficerInitials = computed(() => {
  const member = members.value.find((m) => m.id === form.officerId)
  const name = member?.name ?? props.order.officer.name
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
})

watch(visible, async (isOpen) => {
  if (isOpen) {
    form.description = props.order.description ?? ""
    form.externalFileNumber = props.order.externalFileNumber ?? ""
    form.officerId = props.order.officer.id
    form.verificationEnabled = props.order.verificationEnabled
    form.documentEmailEnabled = props.order.documentEmailEnabled

    loadingMembers.value = true
    try {
      const result = await apolloClient.query({
        query: COMPANY_MEMBERS,
        variables: { id: props.order.company.id },
        fetchPolicy: "network-only",
      })
      members.value = result.data.company.members
    } catch (error) {
      console.error("Failed to load members:", error)
    } finally {
      loadingMembers.value = false
    }
  }
})

const save = async () => {
  isSaving.value = true

  try {
    const variables: Record<string, unknown> = {
      orderId: props.order.gid,
      officerId: form.officerId,
      verificationEnabled: form.verificationEnabled,
      documentEmailEnabled: form.documentEmailEnabled,
    }
    if (!isImported.value) {
      variables.description = form.description
      variables.externalFileNumber = form.externalFileNumber
    }

    const result = await apolloClient.mutate({
      mutation: UPDATE_ORDER,
      variables,
    })

    if (result?.data?.updateOrder?.errors?.length) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: result.data.updateOrder.errors.join(", "),
        life: 5000,
      })
      return
    }

    toast.add({
      severity: "success",
      summary: "Success",
      detail: "Order updated",
      life: 4000,
    })

    visible.value = false
    emit("success")
  } catch (error) {
    console.error("Mutation error:", error)
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to update order",
      life: 5000,
    })
  } finally {
    isSaving.value = false
  }
}
</script>
